import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "stores/connect";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { getConfig } from "components/configurations/helpers";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Button, BootstrapModalForm, Input } from "components/bootstrap";
import { IfPermitted, ISODurationInput } from "components/common";
import Spinner from "components/common/Spinner";
import * as ISODurationUtils from "util/ISODurationUtils";
import { getValueFromInput } from "util/FormsUtils";
const DEFAULT_CONFIG = {
  sidecar_expiration_threshold: "P14D",
  sidecar_inactive_threshold: "PT1M",
  sidecar_update_interval: "PT30S",
  sidecar_send_status: true,
  sidecar_configuration_override: false
};
const SidecarConfig = () => {
  const { t } = useTranslation("configurations");
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(DEFAULT_CONFIG);
  const [formConfig, setFormConfig] = useState(DEFAULT_CONFIG);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    ConfigurationsActions.list(ConfigurationType.SIDECAR_CONFIG).then(() => {
      const config = getConfig(ConfigurationType.SIDECAR_CONFIG, configuration);
      setViewConfig(config);
      setFormConfig(config);
      setLoaded(true);
    });
  }, [configuration]);
  const openModal = () => {
    setShowConfigModal(true);
  };
  const closeModal = () => {
    setShowConfigModal(false);
    setFormConfig(viewConfig);
  };
  const saveConfig = () => {
    ConfigurationsActions.update(ConfigurationType.SIDECAR_CONFIG, formConfig).then(() => {
      closeModal();
    });
  };
  const onUpdate = (field) => (value) => {
    const newValue = typeof value === "object" ? getValueFromInput(value.target) : value;
    setFormConfig({ ...formConfig, [field]: newValue });
  };
  const inactiveThresholdValidator = (milliseconds) => milliseconds >= 1e3;
  const expirationThresholdValidator = (milliseconds) => milliseconds >= 60 * 1e3;
  const durationMilliseconds = (duration) => ISODurationUtils.isValidDuration(duration, (milliseconds) => milliseconds);
  const updateIntervalValidator = (milliseconds) => {
    const inactiveMilliseconds = durationMilliseconds(formConfig.sidecar_inactive_threshold);
    const expirationMilliseconds = durationMilliseconds(formConfig.sidecar_expiration_threshold);
    return milliseconds >= 1e3 && milliseconds < inactiveMilliseconds && milliseconds < expirationMilliseconds;
  };
  if (!loaded || !viewConfig) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("sidecar.title")), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, t("sidecar.inactive_threshold"), ":"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.sidecar_inactive_threshold), /* @__PURE__ */ React.createElement("dt", null, t("sidecar.expiration_threshold"), ":"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.sidecar_expiration_threshold), /* @__PURE__ */ React.createElement("dt", null, t("sidecar.update_interval"), ":"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.sidecar_update_interval), /* @__PURE__ */ React.createElement("dt", null, t("sidecar.send_status"), ":"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.sidecar_send_status ? t("shared.true") : t("shared.false")), /* @__PURE__ */ React.createElement("dt", null, t("sidecar.override_configuration"), ":"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.sidecar_configuration_override ? t("shared.true") : t("shared.false"))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, t("shared.edit_configuration"))), showConfigModal && formConfig && /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      title: t("sidecar.update_title"),
      onSubmitForm: saveConfig,
      onCancel: closeModal,
      submitButtonText: t("shared.update_configuration")
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
      ISODurationInput,
      {
        id: "inactive-threshold-field",
        duration: formConfig.sidecar_inactive_threshold,
        update: onUpdate("sidecar_inactive_threshold"),
        label: t("sidecar.inactive_threshold_label"),
        help: t("sidecar.inactive_threshold_help"),
        validator: inactiveThresholdValidator,
        errorText: t("sidecar.inactive_threshold_error"),
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      ISODurationInput,
      {
        id: "sidecar-expiration-field",
        duration: formConfig.sidecar_expiration_threshold,
        update: onUpdate("sidecar_expiration_threshold"),
        label: t("sidecar.expiration_threshold_label"),
        help: t("sidecar.expiration_threshold_help"),
        validator: expirationThresholdValidator,
        errorText: t("sidecar.expiration_threshold_error"),
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      ISODurationInput,
      {
        id: "sidecar-update-field",
        duration: formConfig.sidecar_update_interval,
        update: onUpdate("sidecar_update_interval"),
        label: t("sidecar.update_interval_label"),
        help: t("sidecar.update_interval_help"),
        validator: updateIntervalValidator,
        errorText: t("sidecar.update_interval_error"),
        required: true
      }
    )),
    /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "send-status-updates-checkbox",
        label: t("sidecar.send_status_updates"),
        checked: formConfig.sidecar_send_status,
        onChange: onUpdate("sidecar_send_status"),
        help: t("sidecar.send_status_updates_help")
      }
    ),
    /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "override-sidecar-config-checkbox",
        label: t("sidecar.override_sidecar_configuration"),
        checked: formConfig.sidecar_configuration_override,
        onChange: onUpdate("sidecar_configuration_override"),
        help: t("sidecar.override_sidecar_configuration_help")
      }
    )
  ));
};
export default SidecarConfig;
